import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router, NavigationEnd} from "@angular/router";
import { Observable, Subscription } from 'rxjs';
import {AuthService} from "@core/services/auth.service";
import { NOTIFICATIONS_PAGE_ROUTE, NotificationService, SchoolNotification } from '@core/services/notification.service';
import { AccountStoreFacade } from 'src/app/store/account/account.facade';
import { GroupStoreFacade } from 'src/app/store/group/group.facade';
import { DeviceStoreFacade } from 'src/app/store/device/device.facade';
import { SchoolUser } from '@core/models/domain/account.domain';
import { AuthStoreFacade } from 'src/app/store/auth/auth.facade';
import { PersistencyService } from '@core/services/persistency.service';
import { StorageAvailabilityService } from '@core/services/storage-availability.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { ContactUsDialogComponent } from '@shared/components/contact-us-dialog/contact-us-dialog.component';
import { buildSchoolInitials } from '@core/utils/school-photo.util';
import { isSchoolAdmin } from '@core/utils/school-role.util';
import { HELP_PAGE_ROUTE, PARENT_RESPONSES_PAGE_ROUTE } from '@core/services/help-routes';
import { PupilDuplicateStoreFacade } from 'src/app/store/pupil-duplicate/pupil-duplicate.facade';
import { PupilLinkStoreFacade } from 'src/app/store/pupil-link/pupil-link.facade';
import { ChildDeactivationStoreFacade } from 'src/app/store/child-deactivation/child-deactivation.facade';
import { WondeSyncStoreFacade } from 'src/app/store/wonde-sync/wonde-sync.facade';

interface MenuItem {
  label: string;
  icon: string;
  route: string;
  title: string;
}

@Component({
  selector: "app-main-layout",
  templateUrl: "./main-layout.component.html",
  styleUrls: ["./main-layout.component.scss", "./main-layout-notifications.scss"],
  standalone: false,
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  unreadNotifications$: Observable<SchoolNotification[]>;
  unreadNotificationCount$: Observable<number>;
  readonly notificationsPageRoute = NOTIFICATIONS_PAGE_ROUTE;
  readonly helpPageRoute = HELP_PAGE_ROUTE;
  readonly parentResponsesPageRoute = PARENT_RESPONSES_PAGE_ROUTE;
  private notificationPolling?: Subscription;
  /** The dashboard's messages read these admin queues; the shell loads them once per session. */
  private adminQueuesLoaded = false;

  userName: string = '';
  userRole: string = '';
  userEmail: string = '';
  profileAvatar: string = '';
  collapsed = true;
  hideAdminMenu = true;
  /** Whether the signed-in staff member may see the Administration nav section. */
  isAdminUser = false;
  // pageName = "Dashboard";
  schoolName = "Default";
  schoolInitials = "SCH";
  /** School photo shown in place of the initials badge when the school set one. */
  schoolLogo = "";

  // Storage / session resilience nudge: 'storage-disabled' (enable storage/cookies)
  // vs 'ids-missing' (session IDs cleared — refresh or sign in again).
  nudge: 'storage-disabled' | 'ids-missing' | null = null;
  nudgeDismissed = false;
  private persistenceAvailable = true;

  menuItems: MenuItem[] = [
    {
      label: "Dashboard",
      icon: "home",
      route: "/dashboards/home",
      title: "Dashboard",
    },
    {
      label: "Notifications",
      icon: "notifications",
      route: NOTIFICATIONS_PAGE_ROUTE,
      title: "Notifications",
    },
    {
      label: "Restrictions",
      icon: "hourglass_empty",
      route: "/restriction/list",
      title: "Manage restrictions",
    },
    {
      label: "Devices",
      icon: "add_to_home_screen",
      route: "/device/list",
      title: "Register a device",
    },
    { label: "Users", icon: "person", route: "/people/users", title: "Users" },
    {
      label: "Groups",
      icon: "group",
      route: "/people/groups",
      title: "Groups",
    },
  ];

  constructor(
    private authStore: AuthStoreFacade,
    private accountStore: AccountStoreFacade,
    private groupStore: GroupStoreFacade,
    private deviceStore: DeviceStoreFacade,
    private router: Router,
    private persistencyService: PersistencyService,
    private storageAvailability: StorageAvailabilityService,
    private authService: AuthService,
     private dialog: MatDialog,
    private notificationService: NotificationService,
    private pupilDuplicateStore: PupilDuplicateStoreFacade,
    private pupilLinkStore: PupilLinkStoreFacade,
    private childDeactivationStore: ChildDeactivationStoreFacade,
    private wondeSyncStore: WondeSyncStoreFacade
  ) {
    this.unreadNotifications$ = this.notificationService.unreadNotifications$;
    this.unreadNotificationCount$ = this.notificationService.unreadCount$;
  }

  ngOnInit(): void {
    console.log("===========> MainLayoutComponent INIT");
    this.accountStore.loadUsers();
    this.accountStore.loadSchoolInfo(this.authStore.getAccountIdsSnapshot().schoolAccountId ?? '');
    this.notificationPolling = this.notificationService.startPolling();
    this.hideAdminMenu = true;
    this.accountStore.schoolInfo$.subscribe((school) => {
      if (school) {
        this.schoolName = school.name;
        this.schoolInitials = this.getSchoolInitials(school.name);
        this.schoolLogo = school.logo ?? "";
      } else {
        console.warn("No school information found, using default name.");
        this.schoolName = "Default School";
        this.schoolInitials = "SCH";
        this.schoolLogo = "";
      }
    });
    // Decide which resilience nudge (if any) to show.
    this.persistenceAvailable = this.storageAvailability.isPersistenceAvailable();
    this.authStore.userAuthentication$.subscribe((auth) => {
      if (!this.persistenceAvailable) {
        this.nudge = 'storage-disabled';
      } else if (!auth?.userAccountId || !auth?.schoolAccountId) {
        this.nudge = 'ids-missing';
      } else {
        this.nudge = null;
      }
    });

    // Fast path: fetch the logged-in user directly so the header (name/role/avatar)
    // renders without waiting for the full get-users roster.
    const userAccountId = this.authStore.getAccountIdsSnapshot().userAccountId;
    if (userAccountId) {
      this.accountStore.fetchUser(userAccountId).subscribe((user) => {
        if (user && user.userInfo) {
          this.applyHeaderUser(user);
        }
      });
    }

    this.accountStore.getCurrentUser().subscribe((user) => {
      if (user && user.userInfo) {
        this.applyHeaderUser(user);
      } else if (!this.userName) {
        this.userName = '';
        this.userEmail = '';
        this.userRole = '';
        this.isAdminUser = false;
      }
    });
  }

  private applyHeaderUser(user: SchoolUser): void {
    this.userName = `${user.userInfo.firstname ?? ''} ${user.userInfo.lastname ?? ''}`.trim();
    this.userEmail = user.userInfo.emailAddress || '';
    // Prefer schoolUserType for role label; fallback to accountType if needed
    this.userRole = (user.schoolUserType || '').toString();
    // The admin section is shown for the roles the backend accepts on admin writes, so a
    // teacher is not offered pages AdminGuard would turn them away from.
    this.isAdminUser = isSchoolAdmin(user);
    // The header user arrives twice (fast path, then the roster), so the queues load once.
    // Only here is the real role known, and the queues' pages are admin only.
    if (this.isAdminUser && !this.adminQueuesLoaded) {
      this.adminQueuesLoaded = true;
      this.pupilDuplicateStore.loadIfNeeded();
      this.pupilLinkStore.loadMismatches();
      this.childDeactivationStore.loadRequests();
      this.wondeSyncStore.loadMismatches();
    }
    // AuthService.isAdmin starts optimistic (true) at login and on session restore, because the
    // login response carries no role. This is the first point the real schoolUserType is known,
    // and every write action in the app (isAuthorized() on the group, device, user and
    // restriction pages) reads that flag, so correct it here.
    this.authService.isAdmin = this.isAdminUser;
    this.profileAvatar = user.userInfo.avatar ? user.userInfo.avatar : 'assets/images/profile.webp';
  }

  ngOnDestroy(): void {
    this.notificationPolling?.unsubscribe();
  }

  openNotification(notification: SchoolNotification): void {
    this.notificationService.markAsRead(notification.id);
    this.router.navigate(notification.link, notification.fragment ? { fragment: notification.fragment } : {});
  }

  markAllNotificationsRead(): void {
    this.notificationService.markAllAsRead();
  }

  /** The menu's way to us, the same dialog the help pages open. */
  openContactUs(): void {
    this.closeMenu();
    this.dialog.open(ContactUsDialogComponent, {
      width: '480px',
      maxWidth: '95vw',
      autoFocus: false,
      panelClass: 'contact-us-dialog',
    });
  }

  dismissNudge(): void {
    this.nudgeDismissed = true;
  }

  // Restore the cleared session IDs from the backend (works once /refresh returns them).
  // The userAuthentication$ subscription clears the nudge if IDs come back.
  onNudgeRefresh(): void {
    this.authStore.hydrateAuth();
  }

  onNudgeSignIn(): void {
    this.authStore.logout();
  }

  getSchoolInitials(schoolName: string): string {
    return buildSchoolInitials(schoolName);
  }

  isHomePage(): boolean {
    return this.router.url === "/dashboards/home";
  }

  isAuthorized(): boolean {
    return this.authService.isAuthorized();
  }

  toggleMenu() {
    this.collapsed = !this.collapsed;
  }

  closeMenu() {
    this.collapsed = true;
  }

  onSidenavClose() {
    this.collapsed = true;
  }

  toggleAdminMenu() {
    this.hideAdminMenu = !this.hideAdminMenu;
  }

  navigateTo(path: string, title: string) {
    this.router.navigate([path]);
  }

  navigateToMenuItem(item: MenuItem) {
    const currentUrl = this.router.url;
    const targetRoute = item.route;

    // Always close menu first
    this.closeMenu();

    // Re-clicking the active menu item used to force a full destroy/recreate of the
    // page (temp-route navigation with route reuse disabled), which re-fired every
    // startup request. Keep the "refresh" gesture, but as a background reload: the
    // page stays mounted and its table stays visible while fresh data arrives.
    if (currentUrl === targetRoute || currentUrl.startsWith(targetRoute + '/')) {
      this.refreshDataForRoute(targetRoute);
    } else {
      // Normal navigation
      this.router.navigate([targetRoute]);
    }
  }

  private refreshDataForRoute(route: string): void {
    if (route.startsWith('/people/users')) {
      this.accountStore.loadUsers();
    } else if (route.startsWith('/people/groups')) {
      this.groupStore.loadGroups();
    } else if (route.startsWith('/device')) {
      this.deviceStore.loadDevices();
    }
    // Dashboard and restriction pages load/poll their own data on entry.
  }

  onLogout() {
    console.log("===========> Logout");
    this.authStore.logout();
  }

  confirmLogout(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Sign Out',
        message: 'Are you sure you want to sign out of your account?',
        confirmText: 'Sign Out',
        cancelText: 'Cancel',
        icon: 'power_settings_new'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.onLogout();
      }
    });
  }
}
